'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  BarChart3,
  Settings,
  Bell,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Building2,
  CreditCard,
  HelpCircle,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const navSections = [
  {
    label: 'Operations',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', href: '/hotel-manager-dashboard', badge: null },
      { icon: CalendarDays, label: 'Bookings', href: '/hotel-manager-dashboard', badge: '3' },
      { icon: Users, label: 'Guests', href: '/hotel-manager-dashboard', badge: null },
    ],
  },
  {
    label: 'Facility',
    items: [
      { icon: Building2, label: 'My Courts', href: '/hotel-manager-dashboard', badge: null },
      { icon: CalendarDays, label: 'Availability', href: '/hotel-manager-dashboard', badge: null },
    ],
  },
  {
    label: 'Finance',
    items: [
      { icon: BarChart3, label: 'Analytics', href: '/hotel-manager-dashboard', badge: null },
      { icon: CreditCard, label: 'Payments', href: '/hotel-manager-dashboard', badge: null },
    ],
  },
  {
    label: 'Account',
    items: [
      { icon: Bell, label: 'Notifications', href: '/hotel-manager-dashboard', badge: '5' },
      { icon: Settings, label: 'Settings', href: '/hotel-manager-dashboard', badge: null },
      { icon: HelpCircle, label: 'Help', href: '/hotel-manager-dashboard', badge: null },
    ],
  },
];

export default function ManagerSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`hidden lg:flex flex-col bg-white border-r border-border transition-all duration-300 ease-in-out flex-shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-border px-4 ${collapsed ? 'justify-center' : 'gap-2.5'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-sans font-semibold text-base tracking-tight text-foreground whitespace-nowrap overflow-hidden">
            Tunisia<span className="text-primary">Padel</span>
          </span>
        )}
      </div>
      {/* Hotel Badge */}
      {!collapsed && (
        <div className="mx-3 mt-3 px-3 py-2.5 bg-primary/8 rounded-xl border border-primary/20">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Managing</p>
          <p className="text-sm font-semibold text-foreground mt-0.5 truncate">Seabel Alhambra</p>
          <p className="text-xs text-muted-foreground truncate">Port El Kantaoui</p>
        </div>
      )}
      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {navSections?.map((section) => (
          <div key={`section-${section?.label}`}>
            {!collapsed && (
              <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground/60">
                {section?.label}
              </p>
            )}
            <div className="space-y-0.5">
              {section?.items?.map((item) => {
                const Icon = item?.icon;
                const isActive = pathname === item?.href;
                return (
                  <Link
                    key={`sidebar-${item?.label}`}
                    href={item?.href}
                    title={collapsed ? item?.label : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group relative ${
                      isActive
                        ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item?.label}</span>
                        {item?.badge && (
                          <span className="flex-shrink-0 min-w-5 h-5 flex items-center justify-center px-1.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                            {item?.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && item?.badge && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
                    )}
                    {/* Tooltip for collapsed */}
                    {collapsed && (
                      <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                        {item?.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      {/* Bottom: User + Collapse */}
      <div className="border-t border-border p-2 space-y-1">
        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-primary">KA</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">Karim Amiri</p>
              <p className="text-xs text-muted-foreground truncate">Padel Manager</p>
            </div>
          </div>
        )}
        <Link
          href="/sign-up-login"
          title={collapsed ? 'Sign Out' : undefined}
          className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-all duration-150 group relative"
        >
          <LogOut size={17} className="flex-shrink-0" />
          {!collapsed && <span>Sign Out</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
              Sign Out
            </span>
          )}
        </Link>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-muted-foreground hover:bg-muted transition-all duration-150"
        >
          {collapsed ? <ChevronRight size={15} /> : <><ChevronLeft size={15} /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}